import { run } from "@mdx-js/mdx";
import { use, useMemo } from "react";
import * as runtime from "react/jsx-runtime";
import { data, Link } from "react-router";
import { toast } from "sonner";
import { BlogHeader } from "#app/components/blog/blog-header.tsx";
import { mdxComponents } from "#app/components/blog/mdx-components.tsx";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import {
	CATEGORY_LABELS,
	type BlogPost,
	type Category,
} from "#app/utils/blog-types.ts";
import {
	getBlogPost,
	getBlogPostList,
} from "#app/utils/blog.server.ts";
import { blogOgImageUrl } from "#app/utils/og/urls.ts";
import { organizationJsonLd } from "#app/utils/structured-data.ts";
import { type Route } from "./+types/$slug.ts";

export async function loader({ params }: Route.LoaderArgs) {
	const { slug } = params;
	if (!slug) throw new Response("Not Found", { status: 404 });

	const post = await getBlogPost(slug);
	if (!post) throw new Response("Not Found", { status: 404 });
	if (post.frontmatter.draft && process.env.NODE_ENV === "production") {
		throw new Response("Not Found", { status: 404 });
	}

	const { posts: relatedPosts } = await getBlogPostList({
		category: post.frontmatter.category,
		limit: 4,
	});
	const related = relatedPosts.filter((p) => p.slug !== slug).slice(0, 3);

	return data({ post, relatedPosts: related });
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data?.post) {
		return [
			{ title: "Post Not Found - shonaslang" },
			{ name: "robots", content: "noindex" },
		];
	}
	const { frontmatter, slug } = data.post;

	return [
		{ title: `${frontmatter.title} - shonaslang Blog` },
		{ name: "description", content: frontmatter.description },
		{ property: "og:title", content: frontmatter.title },
		{ property: "og:description", content: frontmatter.description },
		{ property: "og:type", content: "article" },
		{
			property: "og:url",
			content: `https://shonaslang.com/blog/${slug}`,
		},
		{
			property: "og:image",
			content: blogOgImageUrl(slug),
		},
		{ property: "og:image:width", content: "1200" },
		{ property: "og:image:height", content: "630" },
		{ property: "article:published_time", content: frontmatter.date },
		...(frontmatter.updated
			? [
					{
						property: "article:modified_time",
						content: frontmatter.updated,
					},
				]
			: []),
		...frontmatter.tags.map((tag) => ({
			property: "article:tag",
			content: tag,
		})),
		{ name: "twitter:card", content: "summary_large_image" },
		{ name: "twitter:title", content: frontmatter.title },
		{ name: "twitter:description", content: frontmatter.description },
		{
			name: "twitter:image",
			content: blogOgImageUrl(slug),
		},
		{ name: "author", content: frontmatter.author.name },
	];
};

function MdxRenderer({ code }: { code: string }) {
	const mdxModule = use(
		useMemo(
			() =>
				run(code, {
					...runtime,
					baseUrl: import.meta.url,
				}),
			[code],
		),
	);
	const Content = mdxModule.default;
	return <Content components={mdxComponents} />;
}

function BlogPostingJsonLd({ post }: { post: BlogPost }) {
	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "BlogPosting",
		headline: post.frontmatter.title,
		description: post.frontmatter.description,
		datePublished: post.frontmatter.date,
		dateModified: post.frontmatter.updated ?? post.frontmatter.date,
		author: {
			"@type": "Person",
			name: post.frontmatter.author.name,
		},
		publisher: organizationJsonLd(),
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": `https://shonaslang.com/blog/${post.slug}`,
		},
		image: blogOgImageUrl(post.slug),
		wordCount: post.readingTime.words,
		articleSection: post.frontmatter.category,
		keywords: post.frontmatter.tags.join(", "),
		inLanguage: "en",
	};

	return (
		<script
			type="application/ld+json"
			dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
		/>
	);
}

export default function BlogPostPage({ loaderData }: Route.ComponentProps) {
	const { post, relatedPosts } = loaderData;

	return (
		<div className="container mx-auto max-w-4xl px-4 py-8">
			<BlogPostingJsonLd post={post} />

			{/* Back to blog */}
			<Link
				to="/blog"
				className="mb-6 inline-flex items-center gap-1.5 text-body-xs text-muted-foreground hover:text-foreground"
			>
				<Icon name="arrow-left" className="size-3.5" />
				Back to Blog
			</Link>

			<article>
				<BlogHeader
					frontmatter={post.frontmatter}
					readingTime={post.readingTime}
				/>

				{/* MDX Content */}
				<div className="blog-prose">
					<MdxRenderer code={post.code} />
				</div>
			</article>

			{/* Footer: tags + share */}
			<footer className="mt-12 border-t border-border pt-8">
				{/* Tags */}
				<div className="mb-6 flex flex-wrap items-center gap-2">
					<Icon
						name="tag"
						className="size-4 text-muted-foreground"
					/>
					{post.frontmatter.tags.map((tag) => (
						<Link
							key={tag}
							to={`/blog?tag=${encodeURIComponent(tag)}`}
							className="rounded-full bg-muted px-3 py-1 text-body-2xs text-muted-foreground hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950/30 dark:hover:text-blue-300"
						>
							{tag}
						</Link>
					))}
				</div>

				{/* Share */}
				<div className="flex items-center gap-3">
					<span className="text-body-xs text-muted-foreground">
						Share this post:
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={async () => {
							const url = `https://shonaslang.com/blog/${post.slug}`;
							if (
								typeof navigator !== "undefined" &&
								typeof navigator.share === "function"
							) {
								try {
									await navigator.share({
										title: post.frontmatter.title,
										text: post.frontmatter.description,
										url,
									});
								} catch {
									// user cancelled
								}
							} else {
								await navigator.clipboard.writeText(url);
								toast.success("Link copied!");
							}
						}}
					>
						<Icon name="link-2" size="sm" />
						Share
					</Button>
				</div>

				{/* Related posts */}
				{relatedPosts.length > 0 ? (
					<div className="mt-12">
						<h2
							className="mb-6 text-h4 text-foreground"
							style={{ fontFamily: "var(--serif-head)" }}
						>
							Related Posts
						</h2>
						<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{relatedPosts.map((related) => (
								<Link
									key={related.slug}
									to={`/blog/${related.slug}`}
									className="group rounded-lg border border-border bg-card p-4 no-underline transition-colors hover:border-blue-300 dark:hover:border-blue-700"
								>
									<span className="mb-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-body-2xs text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
										{CATEGORY_LABELS[
											related.frontmatter
												.category as Category
										] ?? related.frontmatter.category}
									</span>
									<h3
										className="text-body-sm font-semibold text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400"
										style={{
											fontFamily: "var(--serif-head)",
										}}
									>
										{related.frontmatter.title}
									</h3>
									<p className="mt-1 text-body-2xs text-muted-foreground line-clamp-2">
										{related.frontmatter.description}
									</p>
								</Link>
							))}
						</div>
					</div>
				) : null}
			</footer>
		</div>
	);
}

export const ErrorBoundary = GeneralErrorBoundary;
